import { httpsCallable, getFunctions } from 'firebase/functions';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface GlossarySyncMetrics {
  processedCount: number;
  newTermsCount: number;
  updatedTermsCount: number;
  unchangedTermsCount: number;
  totalTerms: number;
}

export interface GlossarySyncStatus {
  lastSync?: Date;
  lastSyncSuccess?: boolean;
  error?: string | null;
  inProgress?: boolean;
  startTime?: Date;
  endTime?: Date;
  processedCount?: number;
  newTermsCount?: number;
  updatedTermsCount?: number;
  unchangedTermsCount?: number;
  totalChanges?: number;
  totalTerms?: number;
}

export interface GlossarySyncResult {
  success: boolean;
  result?: GlossarySyncMetrics;
  error?: string;
}

class GlossarySyncService {
  private syncStatusUnsubscribe: Unsubscribe | null = null;

  /**
   * Trigger manual glossary sync from Google Sheets
   */
  async triggerSync(): Promise<GlossarySyncResult> {
    try {
      const functions = getFunctions();
      const triggerGlossarySync = httpsCallable(functions, 'triggerGlossarySync');
      const result = await triggerGlossarySync({});
      
      return result.data as GlossarySyncResult;
    } catch (error) {
      throw new Error(`Failed to trigger sync: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Subscribe to sync status updates
   */
  subscribeSyncStatus(callback: (status: GlossarySyncStatus | null) => void): Unsubscribe {
    const statusRef = doc(db, 'glossarySync', 'latest');
    
    return onSnapshot(statusRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const status: GlossarySyncStatus = {
          lastSync: data.lastSync?.toDate(),
          lastSyncSuccess: data.lastSyncSuccess,
          error: data.error,
          inProgress: data.inProgress,
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
          processedCount: data.processedCount,
          newTermsCount: data.newTermsCount,
          updatedTermsCount: data.updatedTermsCount,
          unchangedTermsCount: data.unchangedTermsCount,
          totalChanges: data.totalChanges,
          totalTerms: data.totalTerms
        };
        callback(status);
      } else {
        callback(null);
      }
    }, (error) => {
      callback(null);
    });
  }

  /**
   * Get current sync status (one-time fetch)
   */
  async getCurrentSyncStatus(): Promise<GlossarySyncStatus | null> {
    try {
      const statusRef = doc(db, 'glossarySync', 'latest');
      const statusDoc = await import('firebase/firestore').then(({ getDoc }) => getDoc(statusRef));
      
      if (statusDoc.exists()) {
        const data = statusDoc.data();
        return {
          lastSync: data.lastSync?.toDate(),
          lastSyncSuccess: data.lastSyncSuccess,
          error: data.error,
          inProgress: data.inProgress,
          startTime: data.startTime?.toDate(),
          endTime: data.endTime?.toDate(),
          processedCount: data.processedCount,
          newTermsCount: data.newTermsCount,
          updatedTermsCount: data.updatedTermsCount,
          unchangedTermsCount: data.unchangedTermsCount,
          totalChanges: data.totalChanges,
          totalTerms: data.totalTerms
        };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Format sync status for display
   */
  formatSyncStatus(status: GlossarySyncStatus | null): string {
    if (!status) return 'No sync data available';
    
    if (status.inProgress) {
      return 'Sync in progress...';
    }
    
    if (status.lastSync) {
      const timeAgo = this.getTimeAgo(status.lastSync);
      if (status.lastSyncSuccess) {
        return `Last synced ${timeAgo} (${status.totalTerms || 0} terms)`;
      } else {
        return `Last sync failed ${timeAgo}`;
      }
    }
    
    return 'Never synced';
  }

  /**
   * Get human-readable time ago string
   */
  private getTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  }
}

export const glossarySyncService = new GlossarySyncService();